import Loader from 'components/Loader';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  AppointmentHistoryItem, QuickBookingItem, QuickService, RecentAppointmentsResult, callerIdService,
} from 'services/caller-id';
import { formatPhone } from 'utils/formatPhone';
import { CallerRef } from './use-caller-id';
import {
  CalendarCheckIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon,
  ClockIcon, CloseIcon, DurationIcon, UserIcon,
} from './icons';
import HistorySheet, { visitDurationMin } from './HistorySheet';
import ServiceSheet from './ServiceSheet';
import {
  ANYONE_COL, SLOTS, UseBookingFormResult, formatDuration, minutesToLabel,
} from './use-booking-form';
import { initialsOf, TAG_PALETTES } from './use-day-availability';

interface BookingPageProps {
  caller: CallerRef;
  form: UseBookingFormResult;
  onBack: () => void;
  onSms: (caller: CallerRef) => void;
  // parent owns the handled/view transition + pending refetch
  onBooked: (caller: CallerRef, summary: string) => void;
  onToast: (msg: string) => void;
}

// State 3 — full-screen booking page sliding in over the calendar. Confirm
// posts quickBooking with callerId so the backend drops the caller from the
// pending list. Unknown callers (no customerId) cannot quickBook yet — the
// create-customer flow is TBD; confirm is blocked with a toast.
const BookingPage = ({ caller, form, onBack, onSms, onBooked, onToast }: BookingPageProps) => {
  const known = caller.customerId !== null;
  const name = caller.customerName || 'Unknown caller';
  const [booking, setBooking] = useState(false);
  // caller bookings use the quick-service catalog, not the shop's regular services
  const [quickServices, setQuickServices] = useState<QuickService[]>([]);

  useEffect(() => {
    let cancelled = false;
    callerIdService.getQuickServices()
      .then(list => { if (!cancelled) setQuickServices(list); })
      .catch(() => { /* sheet shows empty; retry on next form open */ });
    return () => { cancelled = true; };
  }, []);

  // customer brief + up to 2 recent visits (known customers only)
  const [recent, setRecent] = useState<RecentAppointmentsResult | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rebookedId, setRebookedId] = useState<number | null>(null);

  useEffect(() => {
    if (caller.customerId === null) return;
    let cancelled = false;
    callerIdService.getCustomerRecentAppointments(caller.customerId)
      .then(res => { if (!cancelled) setRecent(res); })
      .catch(() => { if (!cancelled) setRecent(null); });
    return () => { cancelled = true; };
  }, [caller.customerId]);

  const {
    weekOffset, dateOptions, selectedDate, pickDate, prevWeek, nextWeek,
    staff, isSlotFree, busyType,
    selectedStaff, selectedTime, durationMin, isSlotSelected, tapCell,
    services, toggleService, removeService, applyRebook,
    note, setNote,
    serviceSheetOpen, setServiceSheetOpen,
  } = form;

  const staffLabel = selectedStaff === ANYONE_COL
    ? 'Anyone'
    : staff.find(s => s.id === selectedStaff)?.label ?? '—';

  const confirmBook = () => {
    if (!selectedTime) return onToast('Select a start time before booking.');
    if (caller.customerId === null) {
      return onToast('Customer has no profile yet — please use SMS.');
    }
    if (services.length === 0) return onToast('Select at least one service.');
    if (booking) return;
    setBooking(true);
    const isAnybody = selectedStaff === ANYONE_COL || selectedStaff === null;
    const item: QuickBookingItem = {
      customerId: caller.customerId,
      staffId: isAnybody ? 0 : Number(selectedStaff),
      isAnybody: isAnybody ? 1 : 0,
      requestStaff: !isAnybody,
      bookingType: 0,
      startTime: `${moment(selectedDate.iso).format('MM/DD/YYYY')} ${String(Math.floor(selectedTime.minutes / 60)).padStart(2, '0')}:${String(selectedTime.minutes % 60).padStart(2, '0')}`,
      duration: durationMin,
      totalPeople: 1,
      note,
      services: services
        .map(id => quickServices.find(s => s.id === id))
        .filter((s): s is QuickService => !!s)
        .map(s => ({ id: s.id, serviceName: s.serviceName, price: s.price })),
      callerId: caller.callerId,
    };
    callerIdService.createBooking(item)
      .then(() => onBooked(caller, `Booked: ${name} · ${selectedTime.label} · ${staffLabel}`))
      .catch(() => onToast('Booking failed, please try again.'))
      .finally(() => setBooking(false));
  };

  // Re book: match past serviceNames to the quick-service catalog (names only
  // in history); duration from the visit's start/end
  const rebook = (item: AppointmentHistoryItem) => {
    const ids = item.serviceNames
      .map(n => quickServices.find(s => s.serviceName.trim().toLowerCase() === n.trim().toLowerCase()))
      .filter((s): s is QuickService => !!s)
      .map(s => s.id);
    applyRebook(ids, visitDurationMin(item));
    setRebookedId(item.id);
    setHistoryOpen(false);
    const unmatched = item.serviceNames.length - ids.length;
    if (unmatched > 0) onToast(`${unmatched} past service(s) no longer in the catalog.`);
  };

  const columns = [
    { id: ANYONE_COL, label: 'Anyone', tag: 'AN', tagBg: '#F2F4F8', tagFg: '#6B7280' },
    ...staff,
  ];
  // real shops have arbitrary staff counts — the design's fixed 6 columns are a
  // prototype artifact; extra columns scroll horizontally inside the grid box
  const gridTemplateColumns = `50px repeat(${columns.length}, 58px)`;

  const historyCount = recent?.recentAppointments.length ?? 0;

  return (
    <div className="cid-form-page">
      {booking && <Loader />}
      <div className="cid-form-header">
        <button type="button" className="cid-form-back" onClick={onBack}><ChevronLeftIcon size={18} /></button>
        <span className="cid-form-title">Book Appointment</span>
      </div>

      <div className="cid-form-body">
        {/* customer header */}
        <div className="cid-form-customer">
          <span className="cid-form-avatar">{known ? initialsOf(name) : '?'}</span>
          <span className="cid-form-customer-info">
            <span className="cid-form-customer-name-row">
              <span className="cid-form-customer-name">{name}</span>
              {(!known || caller.justCreated) && <span className="cid-badge-new large">new customer</span>}
            </span>
            <span className="cid-form-customer-phone">{formatPhone(caller.phone)}</span>
          </span>
          {historyCount > 0 && (
            <button type="button" className="cid-history-btn" onClick={() => setHistoryOpen(true)}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5M12 7v5l4 2" /></svg>
              <span className="badge">{historyCount}</span>
            </button>
          )}
        </div>

        {/* read-only summary */}
        <div className="cid-form-summary">
          <div className="cid-form-summary-item">
            <CalendarIcon size={14} /><span className="k">Date</span>
            <span className="v">{`${selectedDate.iso.slice(8)}-${selectedDate.iso.slice(5, 7)}`}</span>
          </div>
          <div className="cid-form-summary-item">
            <UserIcon size={14} /><span className="k">NV</span>
            <span className="v ellipsis">{selectedStaff ? staffLabel : '—'}</span>
          </div>
          <div className="cid-form-summary-item">
            <ClockIcon size={14} /><span className="k">Time</span>
            <span className={`v ${selectedTime ? '' : 'unset'}`}>{selectedTime ? selectedTime.label : 'Select time'}</span>
          </div>
          <div className="cid-form-summary-item">
            <DurationIcon size={14} /><span className="k">TL</span>
            <span className="v">{formatDuration(durationMin)}</span>
          </div>
        </div>
        <div className="cid-form-hint">Pick a time and staff member in the grid below.</div>

        {/* date strip */}
        <div className="cid-date-strip">
          <span className="cid-date-strip-icon"><CalendarIcon size={15} /></span>
          <span className="cid-date-strip-label">{selectedDate.full}</span>
          <button type="button" className="cid-date-nav" disabled={weekOffset === 0} onClick={prevWeek}><ChevronLeftIcon size={14} /></button>
          <button type="button" className="cid-date-nav" onClick={nextWeek}><ChevronRightIcon size={14} /></button>
        </div>
        <div className="cid-date-chips">
          {dateOptions.map(d => (
            <button key={d.iso} type="button" className={`cid-date-chip ${d.iso === selectedDate.iso ? 'selected' : ''}`} onClick={() => pickDate(d)}>
              <span className="dow">{d.dow}</span>
              <span className="day">{d.day}</span>
            </button>
          ))}
        </div>

        {/* staff × time availability grid */}
        <div className="cid-grid">
          <div className="cid-grid-header" style={{ gridTemplateColumns }}>
            <div className="cid-grid-corner" />
            {columns.map(col => (
              <div key={col.id} className="cid-grid-col-head">
                <span className="dot" style={{ background: col.tagBg, color: col.tagFg }}>{col.tag}</span>
                <span className="label">{col.label}</span>
              </div>
            ))}
          </div>
          {SLOTS.map(slot => (
            <div key={slot} className="cid-grid-row" style={{ gridTemplateColumns }}>
              <div className="cid-grid-time">{minutesToLabel(slot)}</div>
              {columns.map(col => {
                const busy = col.id === ANYONE_COL ? undefined : busyType(col.id, slot);
                const free = isSlotFree(col.id, slot);
                const selected = isSlotSelected(col.id, slot);
                const cls = selected ? 'selected' : busy === 'appt' ? 'busy' : busy === 'block' ? 'block' : free ? 'free' : 'block';
                return (
                  <div key={col.id} className={`cid-grid-cell ${cls}`} onClick={() => tapCell(col.id, slot)}>
                    {selected && (
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m20 6-11 11-5-5" /></svg>
                    )}
                    {!selected && busy === 'appt' && <span className="busy-icon"><CalendarCheckIcon size={13} /></span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cid-grid-caption">
          Tap 1 = pick staff + start time · tap 2 (same column) = sets the duration. Shaded cells = already booked; hatched cells = break/blocked hours.
        </div>

        {/* service pills */}
        <div className="cid-service-pills">
          {services.map(id => {
            const svc = quickServices.find(s => s.id === id);
            if (!svc) return null;
            const palette = TAG_PALETTES[quickServices.indexOf(svc) % TAG_PALETTES.length];
            return (
              <span key={id} className="cid-service-pill">
                <span className="dot" style={{ background: palette.bg, color: palette.fg }}>{initialsOf(svc.serviceName)}</span>
                {svc.serviceName}
                <span className="remove" onClick={() => removeService(id)}><CloseIcon size={13} /></span>
              </span>
            );
          })}
          <button type="button" className="cid-add-service" onClick={() => setServiceSheetOpen(true)}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
            Add service
          </button>
        </div>

        {/* note */}
        <div className="cid-form-note">
          <div className="cid-form-note-label">Note</div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Add a note for this appointment…"
          />
        </div>
      </div>

      {/* sticky footer */}
      <div className="cid-form-footer">
        <button type="button" className="cid-btn-outline" onClick={onBack}>Back</button>
        <button type="button" className="cid-btn-outline" onClick={() => onSms(caller)}>SMS</button>
        <button type="button" className={`cid-btn-confirm ${selectedTime && known ? '' : 'disabled'}`} onClick={confirmBook}>Book</button>
      </div>

      {serviceSheetOpen && (
        <ServiceSheet services={quickServices} selected={services} onToggle={toggleService} onClose={() => setServiceSheetOpen(false)} />
      )}
      {historyOpen && caller.customerId !== null && recent && (
        <HistorySheet
          customerId={caller.customerId}
          customerName={recent.customer.name || name}
          recent={recent.recentAppointments}
          rebookedId={rebookedId}
          onRebook={rebook}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
};

export default BookingPage;
